/**
 * FRONT-3004: the one approval card, shared by chat, the side panel and the
 * phone — same words, same order, same buttons (D4 on the decision page).
 *
 * It reads top to bottom: what EDDY will do (a headline built from facts),
 * why (EDDY's own words, labelled), the facts that matter for this kind of
 * action, a preview, the buttons, and a quiet footer saying what EDDY asks
 * before by default, with a link to change it. How serious it looks follows
 * the kind of action (D2): routine work is calm and compact, a card that
 * reaches people or changes who can see something is amber, a deletion is red
 * with the safe button taking focus.
 *
 * Colour never carries the meaning alone: every non-routine kind has an icon
 * and its buttons name the action.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Eye, PaperPlaneTilt, Trash } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import type { Approval } from '~/hooks/queries/use-approvals';
import { systemSettingsPath } from '~/lib/system-settings-sections';
import { technicalDetailsOf, type ApprovalCardKind, type ApprovalCardView } from './approval-card-view';

const FRAME: Record<ApprovalCardKind, string> = {
  routine: 'border-border bg-muted/40',
  reaches_people: 'border-amber-500/30 bg-amber-500/[0.07] dark:bg-amber-500/[0.05]',
  visibility: 'border-amber-500/30 bg-amber-500/[0.07] dark:bg-amber-500/[0.05]',
  deletes: 'border-destructive/30 bg-destructive/[0.06]',
};

const ICON: Record<ApprovalCardKind, ReactNode> = {
  routine: null,
  reaches_people: <PaperPlaneTilt size={16} weight="regular" className="text-amber-700 dark:text-amber-400" aria-hidden />,
  visibility: <Eye size={16} weight="regular" className="text-amber-700 dark:text-amber-400" aria-hidden />,
  deletes: <Trash size={16} weight="regular" className="text-destructive" aria-hidden />,
};

/** The card's outer frame classes for its kind. */
export function approvalFrameClass(kind: ApprovalCardKind): string {
  return `rounded-xl border px-3.5 py-3 ${FRAME[kind]}`;
}

/** Headline, why, facts, preview and the folded technical details. */
export function ApprovalCardBody({ approval, card }: { approval: Approval; card: ApprovalCardView }) {
  const technical = technicalDetailsOf(approval, card);
  const icon = ICON[card.kind];
  return (
    <div className="flex gap-2.5">
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">
        <p
          data-testid="approval-description"
          className="text-[length:var(--text-base)] text-foreground leading-snug max-h-60 overflow-y-auto break-words [overflow-wrap:anywhere]"
        >
          {card.subject ? (
            <>
              {card.title} <span className="font-semibold">{card.subject}</span>
            </>
          ) : (
            <span className="font-medium">{card.title}</span>
          )}
        </p>
        {card.why && (
          <p data-testid="approval-why" className="mt-1 text-[length:var(--text-sm)] text-muted-foreground leading-snug text-pretty">
            Why: {card.why}
          </p>
        )}
        {card.facts.length > 0 && (
          <dl data-testid="approval-facts" className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[length:var(--text-sm)]">
            {card.facts.map((f) => (
              <div key={`${f.label}:${f.value}`} className="contents">
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="text-foreground break-words [overflow-wrap:anywhere]">{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {card.preview &&
          (card.preview.open ? (
            <div
              data-testid="approval-preview"
              className={`mt-2 rounded-md border border-border bg-background/80 p-2 text-[length:var(--text-sm)] text-foreground whitespace-pre-wrap break-words max-h-48 overflow-y-auto ${card.preview.mono ? 'font-mono' : ''}`}
            >
              {card.preview.text}
            </div>
          ) : (
            <details data-testid="approval-preview" className="mt-2">
              <summary className="cursor-pointer text-[length:var(--text-sm)] text-muted-foreground">{card.preview.label}</summary>
              <pre
                className={`mt-1.5 p-2 rounded bg-background/70 border border-border text-[length:var(--text-sm)] text-muted-foreground overflow-x-auto whitespace-pre-wrap break-words max-h-48 ${card.preview.mono ? 'font-mono' : 'font-sans'}`}
              >
                {card.preview.text}
              </pre>
            </details>
          ))}
        {technical && (
          <details className="mt-2">
            <summary className="cursor-pointer text-[length:var(--text-xs)] text-muted-foreground">Technical details</summary>
            <pre className="mt-1.5 p-2 rounded bg-background/70 border border-border text-[length:var(--text-xs)] font-mono text-muted-foreground overflow-x-auto whitespace-pre-wrap break-all max-h-40">
              {technical}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

/**
 * The confirm and cancel buttons, confirm first on every screen. A deletion's
 * confirm is red and its cancel takes focus — but only when nothing else has
 * focus, so a card arriving never pulls the cursor out of the chat box.
 */
export function ApprovalCardButtons({
  card,
  busy,
  busyLabel = 'Working…',
  confirmLabel,
  onConfirm,
  onCancel,
  children,
}: {
  card: ApprovalCardView;
  busy?: boolean;
  busyLabel?: string;
  /** Overrides the card's own label (the plan offer's "only this one"). */
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Extra buttons between confirm and cancel (the plan offer) or after them (Edit). */
  children?: { afterConfirm?: ReactNode; afterCancel?: ReactNode };
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const deletes = card.kind === 'deletes';
  useEffect(() => {
    if (!deletes) return;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (!active || active === document.body) cancelRef.current?.focus({ preventScroll: true });
  }, [deletes]);
  return (
    <div data-testid="approval-actions" className="flex flex-wrap gap-2 mt-3">
      <Button
        data-testid="approval-confirm"
        size="sm"
        variant={deletes ? 'destructive' : 'default'}
        disabled={busy}
        className="h-8 text-[length:var(--text-sm)]"
        onClick={onConfirm}
      >
        {busy ? busyLabel : (confirmLabel ?? card.confirmLabel)}
      </Button>
      {children?.afterConfirm}
      <Button
        ref={cancelRef}
        data-testid="approval-cancel"
        size="sm"
        variant="outline"
        disabled={busy}
        className="h-8 text-[length:var(--text-sm)]"
        onClick={onCancel}
      >
        {card.cancelLabel}
      </Button>
      {children?.afterCancel}
    </div>
  );
}

/** "EDDY asks before saving files · Change": what EDDY does by default, and where to change it. */
export function ApprovalCardFooter({ card, className = 'mt-2.5' }: { card: ApprovalCardView; className?: string }) {
  if (!card.policyRule && !card.askPhrase) return null;
  const sentence = card.askPhrase ? `EDDY asks before ${card.askPhrase}` : `EDDY asks first for “${card.policyRule}”`;
  return (
    <p data-testid="approval-policy-reason" className={`${className} text-[length:var(--text-xs)] text-muted-foreground leading-snug`}>
      {sentence}
      {card.policyRule && (
        <>
          {' · '}
          <a
            href={systemSettingsPath('policy')}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Change “${card.policyRule}” in Policy`}
            className="text-foreground underline underline-offset-2 hover:text-primary"
          >
            Change
          </a>
        </>
      )}
    </p>
  );
}
